import { useRef, useState } from "react";
import api, { downloadFile, formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { AlertTriangle, CheckCircle2, FileDown, Loader2, Undo2, UploadCloud } from "lucide-react";

// PROMPT 132 (2026-10-16): cargar muchas preguntas típicas de una vez (pegando texto o subiendo un
// Excel / CSV). Tres pasos: 1) pegar o subir, 2) vista previa (no guarda nada: se ve cuáles son nuevas,
// cuáles ya estaban y cuáles tienen problemas) y 3) el resultado, con la opción de deshacer toda la carga.
// El servidor vuelve a revisar todo al guardar. Solo crea: una pregunta que ya existe no se cambia acá.

const CAMPO = "h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-700";
const COLUMNA = { pregunta: "Pregunta", respuesta: "Respuesta", tema: "Tema", marca: "Marca" };
const ESTADO = {
  nueva: { texto: "Nueva", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  ya_existe: { texto: "Ya está cargada", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  repetida: { texto: "Repetida", cls: "bg-slate-100 text-slate-600 border-slate-200" },
  sin_lugar: { texto: "Sin lugar", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  error: { texto: "Hay que revisarla", cls: "bg-amber-50 text-amber-700 border-amber-200" },
};

const plural = (n, uno, varios) => `${n} ${n === 1 ? uno : varios}`;
const recortar = (t, n) => (t && t.length > n ? `${t.slice(0, n).trim()}…` : t || "");

const EJEMPLO =
  "Tema: Equipaje\nP: ¿Cuántas maletas puedo llevar?\nR: Una de hasta 23 kg y un bolso de mano.\n" +
  "P: ¿Puedo llevar un cochecito?\nR: Sí, se guarda en el bus sin costo.\n\n" +
  "Tema: Pagos\nP: ¿Aceptan tarjeta?\nR: Sí, Visa y Mastercard.";

export default function CargarPreguntasDialog({ open, onOpenChange, marcas = [], temas = [], onCargado }) {
  const [modo, setModo] = useState("texto"); // texto | archivo
  const [texto, setTexto] = useState("");
  const [archivo, setArchivo] = useState(null);
  const [marca, setMarca] = useState("");
  const [tema, setTema] = useState("");
  const [vista, setVista] = useState(null); // lo que dice el servidor de la vista previa
  const [seleccion, setSeleccion] = useState(new Set());
  const [leyendo, setLeyendo] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [resultado, setResultado] = useState(null);
  const [deshecha, setDeshecha] = useState(null);
  const [confirmaDeshacer, setConfirmaDeshacer] = useState(false);
  const [deshaciendo, setDeshaciendo] = useState(false);
  const archivoRef = useRef(null);

  const nombreMarca = (m) => m.remitente_nombre || m.nombre || m.slug;
  const nombreDeLaMarca = (slug) => {
    const m = marcas.find((x) => x.slug === slug);
    return m ? nombreMarca(m) : slug;
  };

  const hayAlgo = modo === "texto" ? texto.trim().length > 0 : Boolean(archivo);

  const descargarPlantilla = async () => {
    try {
      await downloadFile("/whatsapp/preguntas/plantilla", "plantilla_preguntas_tipicas.xlsx");
      toast.success("Plantilla descargada.");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const verVistaPrevia = async () => {
    setLeyendo(true);
    try {
      let data;
      if (modo === "archivo") {
        const fd = new FormData();
        fd.append("archivo", archivo);
        if (marca) fd.append("marca_slug", marca);
        if (tema.trim()) fd.append("tema", tema.trim());
        ({ data } = await api.post("/whatsapp/preguntas/lote/vista-previa-archivo", fd));
      } else {
        ({ data } = await api.post("/whatsapp/preguntas/lote/vista-previa", {
          texto,
          marca_slug: marca || null,
          tema: tema.trim() || null,
        }));
      }
      setVista(data);
      setSeleccion(new Set(data.filas.map((f, i) => (f.estado === "nueva" ? i : -1)).filter((i) => i >= 0)));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLeyendo(false);
    }
  };

  const alternar = (i) =>
    setSeleccion((s) => {
      const c = new Set(s);
      if (c.has(i)) c.delete(i);
      else c.add(i);
      return c;
    });

  const todasLasNuevas = () => (vista ? vista.filas.map((f, i) => (f.estado === "nueva" ? i : -1)).filter((i) => i >= 0) : []);

  const guardar = async () => {
    const filas = vista.filas
      .map((f, i) => ({ f, i }))
      .filter(({ i }) => seleccion.has(i))
      .map(({ f }) => ({
        n: f.n,
        pregunta: f.pregunta,
        respuesta: f.respuesta,
        tema: f.tema,
        marca_slug: f.alcance === "marca" ? f.marca_slug : "",
      }));
    setGuardando(true);
    try {
      const { data } = await api.post("/whatsapp/preguntas/lote", {
        filas,
        archivo: modo === "archivo" && archivo ? archivo.name : null,
      });
      setResultado(data);
      setDeshecha(null);
      setConfirmaDeshacer(false);
      if (data.creadas) toast.success(`Listo, el agente ya sabe ${plural(data.creadas, "pregunta nueva", "preguntas nuevas")}.`);
      onCargado?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setGuardando(false);
    }
  };

  const deshacer = async () => {
    setDeshaciendo(true);
    try {
      const { data } = await api.post(`/whatsapp/preguntas/lote/${resultado.lote_id}/deshacer`);
      setDeshecha(data.apagadas);
      setConfirmaDeshacer(false);
      toast.success(`Listo: se apagaron ${plural(data.apagadas, "pregunta", "preguntas")} de esa carga.`);
      onCargado?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setDeshaciendo(false);
    }
  };

  const otraCarga = () => {
    setResultado(null);
    setVista(null);
    setSeleccion(new Set());
    setTexto("");
    setArchivo(null);
    setDeshecha(null);
    setConfirmaDeshacer(false);
    if (archivoRef.current) archivoRef.current.value = "";
  };

  const lugarQueQueda = vista ? Math.max(0, vista.cupo.lugar - seleccion.size) : 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="cargar-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <UploadCloud className="h-5 w-5" /> Cargar muchas preguntas de una vez
          </DialogTitle>
          <DialogDescription>
            Pegá las preguntas con sus respuestas o subí un Excel. Antes de guardar vas a ver cómo queda cada una: no se
            carga nada hasta que lo confirmes.
          </DialogDescription>
        </DialogHeader>

        {resultado ? (
          <div className="space-y-3" data-testid="cargar-resultado">
            {resultado.creadas ? (
              <p className="flex items-start gap-2 text-sm text-slate-800" data-testid="cargar-resultado-creadas">
                <CheckCircle2 className="h-5 w-5 shrink-0 text-emerald-600" />
                <span>
                  Listo: se {resultado.creadas === 1 ? "cargó" : "cargaron"} {plural(resultado.creadas, "pregunta", "preguntas")}. El
                  agente las usa desde la próxima conversación.
                </span>
              </p>
            ) : (
              <p className="flex items-start gap-2 text-sm text-slate-800" data-testid="cargar-resultado-ninguna">
                <AlertTriangle className="h-5 w-5 shrink-0 text-amber-600" />
                <span>No se cargó ninguna pregunta.</span>
              </p>
            )}
            {resultado.omitidas?.length > 0 && (
              <div className="rounded-md border border-slate-200 bg-slate-50/60 px-3 py-2 space-y-1" data-testid="cargar-omitidas">
                <p className="text-xs font-medium text-slate-700">
                  No se {resultado.omitidas.length === 1 ? "cargó" : "cargaron"} {plural(resultado.omitidas.length, "pregunta", "preguntas")}:
                </p>
                {resultado.omitidas.map((o, i) => (
                  <p key={i} className="text-xs text-slate-600">
                    <span className="text-slate-400">#{o.n}</span> {recortar(o.pregunta, 90) || "(sin pregunta)"} — {o.motivo}
                  </p>
                ))}
              </div>
            )}
            {deshecha !== null && (
              <p className="text-sm text-slate-700" data-testid="cargar-deshecha">
                Se {deshecha === 1 ? "apagó" : "apagaron"} {plural(deshecha, "pregunta", "preguntas")} de esta carga. No se borraron: se pueden volver a
                prender una por una desde «Apagadas».
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              {resultado.lote_id && deshecha === null && !confirmaDeshacer && (
                <Button variant="outline" onClick={() => setConfirmaDeshacer(true)} data-testid="cargar-deshacer" className="gap-1.5">
                  <Undo2 className="h-4 w-4" /> Deshacer esta carga
                </Button>
              )}
              {confirmaDeshacer && (
                <>
                  <span className="self-center text-xs text-slate-600">
                    ¿Apagar {plural(resultado.creadas, "pregunta", "preguntas")} de esta carga?
                  </span>
                  <Button onClick={deshacer} disabled={deshaciendo} data-testid="cargar-deshacer-confirmar" className="gap-1.5">
                    {deshaciendo ? <Loader2 className="h-4 w-4 animate-spin" /> : <Undo2 className="h-4 w-4" />} Sí, apagarlas
                  </Button>
                  <Button variant="ghost" onClick={() => setConfirmaDeshacer(false)} data-testid="cargar-deshacer-no">
                    No
                  </Button>
                </>
              )}
              <Button variant="outline" onClick={otraCarga} data-testid="cargar-otra">
                Cargar otras
              </Button>
              <Button onClick={() => onOpenChange(false)} data-testid="cargar-cerrar">
                Cerrar
              </Button>
            </div>
          </div>
        ) : !vista ? (
          <div className="space-y-3" data-testid="cargar-paso-entrada">
            <div className="flex items-center gap-2 flex-wrap">
              {[
                ["texto", "Pegar texto"],
                ["archivo", "Subir un archivo"],
              ].map(([clave, nombre]) => (
                <button
                  key={clave}
                  type="button"
                  onClick={() => setModo(clave)}
                  data-testid={`cargar-modo-${clave}`}
                  className={`rounded-full border px-3 py-1 text-xs ${
                    modo === clave
                      ? "border-slate-900 bg-slate-900 text-white"
                      : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"
                  }`}
                >
                  {nombre}
                </button>
              ))}
            </div>
            {modo === "texto" ? (
              <div className="space-y-1.5">
                <textarea
                  value={texto}
                  onChange={(e) => setTexto(e.target.value)}
                  rows={10}
                  className="w-full rounded-md border border-slate-200 px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-slate-900/10"
                  placeholder={EJEMPLO}
                  data-testid="cargar-texto"
                />
                <details className="text-xs text-slate-600" data-testid="cargar-ayuda">
                  <summary className="cursor-pointer text-slate-700">¿Cómo se pegan?</summary>
                  <div className="mt-1.5 space-y-1.5">
                    <p>
                      Una pregunta tras otra: «P:» y la pregunta, y abajo «R:» y la respuesta (también sirven «Pregunta:» y
                      «Respuesta:»). La respuesta puede seguir en las líneas de abajo.
                    </p>
                    <p>
                      Para ordenarlas, poné una línea «Tema: Equipaje» (o «Marca: Vacaciones») arriba de un grupo de preguntas:
                      vale para todas las que siguen, hasta que pongas otra. «Marca: Todas» las deja para todas las marcas.
                    </p>
                    <p>
                      También podés copiar las celdas de una planilla (Excel o Google Sheets): columnas Pregunta, Respuesta y,
                      si querés, Tema y Marca.
                    </p>
                  </div>
                </details>
              </div>
            ) : (
              <div className="space-y-2">
                <Input
                  ref={archivoRef}
                  type="file"
                  accept=".xlsx,.csv,.txt"
                  onChange={(e) => setArchivo(e.target.files?.[0] || null)}
                  data-testid="cargar-archivo"
                />
                <p className="text-[11px] text-slate-400">
                  Excel (.xlsx), CSV o un .txt con «P:» y «R:». Hasta 2 MB. Las columnas son Pregunta, Respuesta y, si querés, Tema y
                  Marca.
                </p>
                <Button variant="outline" size="sm" onClick={descargarPlantilla} data-testid="cargar-plantilla" className="gap-2">
                  <FileDown className="h-4 w-4" /> Descargar la plantilla (Excel)
                </Button>
              </div>
            )}
            <div className="rounded-md border border-slate-200 bg-slate-50/60 p-3 space-y-2">
              <p className="text-xs text-slate-600">Para las preguntas que no traigan su marca o su tema:</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="cargar-marca" className="text-xs">Marca</Label>
                  <select
                    id="cargar-marca"
                    value={marca}
                    onChange={(e) => setMarca(e.target.value)}
                    className={CAMPO}
                    data-testid="cargar-marca"
                  >
                    <option value="">Todas las marcas</option>
                    {marcas.map((m) => (
                      <option key={m.slug} value={m.slug}>{nombreMarca(m)}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="cargar-tema" className="text-xs">Tema</Label>
                  <Input
                    id="cargar-tema"
                    list="cargar-temas"
                    value={tema}
                    onChange={(e) => setTema(e.target.value)}
                    maxLength={40}
                    placeholder="Sin tema"
                    data-testid="cargar-tema"
                  />
                  <datalist id="cargar-temas">
                    {temas.map((t) => (
                      <option key={t} value={t} />
                    ))}
                  </datalist>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cargar-cancelar">
                Cancelar
              </Button>
              <Button onClick={verVistaPrevia} disabled={!hayAlgo || leyendo} data-testid="cargar-vista-previa" className="gap-1.5">
                {leyendo ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                Ver cómo queda
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3" data-testid="cargar-paso-vista">
            <div className="space-y-1" data-testid="cargar-resumen">
              <p className="text-sm text-slate-800">
                {plural(vista.total, "pregunta leída", "preguntas leídas")}:{" "}
                <span className="text-emerald-700">{plural(vista.nuevas, "nueva", "nuevas")}</span>
                {vista.ya_existen ? <> · <span className="text-sky-700">{vista.ya_existen} ya cargada{vista.ya_existen === 1 ? "" : "s"}</span></> : null}
                {vista.repetidas ? <> · <span className="text-slate-600">{plural(vista.repetidas, "repetida", "repetidas")}</span></> : null}
                {vista.con_problemas ? <> · <span className="text-amber-700">{plural(vista.con_problemas, "con problemas", "con problemas")}</span></> : null}
              </p>
              <p className="text-[11px] text-slate-400">
                {vista.columnas
                  ? `Columnas que leí: ${vista.columnas.map((c) => COLUMNA[c] || c).join(" · ")}.`
                  : "Leí el texto con «P:» y «R:»."}{" "}
                Solo se cargan las nuevas; las que ya estaban no se cambian (para corregirlas, usá «Editar» en la lista).
              </p>
              {vista.ignoradas > 0 && (
                <p className="text-[11px] text-amber-700" data-testid="cargar-ignoradas">
                  {plural(vista.ignoradas, "línea del texto no se tuvo", "líneas del texto no se tuvieron")} en cuenta (no seguían a ninguna
                  pregunta, respuesta, tema o marca).
                </p>
              )}
              <p className="text-[11px] text-slate-400" data-testid="cargar-cupo">
                El agente lee hasta {vista.cupo.maximo} entradas: hay {vista.cupo.activas} cargadas y quedan {lugarQueQueda} lugares después de
                esta carga.
              </p>
            </div>
            <div className="flex items-center justify-between gap-2 px-1">
              <label className="flex items-center gap-2 text-xs text-slate-600">
                <Checkbox
                  checked={vista.nuevas > 0 && seleccion.size === vista.nuevas}
                  onCheckedChange={(v) => setSeleccion(v ? new Set(todasLasNuevas()) : new Set())}
                  disabled={vista.nuevas === 0}
                  data-testid="cargar-todas"
                />
                Todas las nuevas
              </label>
              <span className="text-xs text-slate-500" data-testid="cargar-seleccionadas">
                {plural(seleccion.size, "seleccionada", "seleccionadas")}
              </span>
            </div>
            <div className="max-h-[42vh] overflow-y-auto space-y-2 pr-1" data-testid="cargar-filas">
              {vista.filas.map((f, i) => {
                const est = ESTADO[f.estado] || ESTADO.error;
                return (
                  <div
                    key={i}
                    className={`rounded-md border px-3 py-2 ${f.estado === "nueva" ? "border-slate-200 bg-white" : "border-slate-200 bg-slate-50/70"}`}
                    data-testid={`cargar-fila-${i}`}
                    data-estado={f.estado}
                  >
                    <div className="flex items-start gap-2.5">
                      <Checkbox
                        checked={seleccion.has(i)}
                        onCheckedChange={() => alternar(i)}
                        disabled={f.estado !== "nueva"}
                        className="mt-0.5"
                        data-testid={`cargar-fila-check-${i}`}
                      />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span className="text-[10px] text-slate-400">#{f.n}</span>
                          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border ${est.cls}`}>
                            {est.texto}
                          </span>
                          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-sky-50 text-sky-700 border-sky-200">
                            {f.alcance === "marca" ? nombreDeLaMarca(f.marca_slug) : "Todas las marcas"}
                          </span>
                          {f.tema ? (
                            <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium border bg-slate-100 text-slate-600 border-slate-200">
                              {f.tema}
                            </span>
                          ) : null}
                        </div>
                        <p className="text-sm font-medium text-slate-900 mt-1 break-words">{f.pregunta || "(sin pregunta)"}</p>
                        <p className="text-sm text-slate-600 mt-0.5 whitespace-pre-wrap break-words">{recortar(f.respuesta, 260) || "(sin respuesta)"}</p>
                        {f.estado !== "nueva" && f.problemas?.length > 0 && (
                          <p className="text-xs text-amber-700 mt-1" data-testid={`cargar-fila-motivo-${i}`}>
                            {f.problemas.join(" ")}
                          </p>
                        )}
                        {f.estado === "ya_existe" && f.existente && f.problemas?.[0]?.includes("otra respuesta") && (
                          <p className="text-[11px] text-slate-500 mt-0.5 break-words">Ahora dice: «{recortar(f.existente.respuesta, 160)}»</p>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            {vista.nuevas === 0 && (
              <p className="text-xs text-amber-700" data-testid="cargar-nada-nuevo">
                No hay nada nuevo para cargar. Volvé para corregir el texto o el archivo.
              </p>
            )}
            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" onClick={() => onOpenChange(false)} data-testid="cargar-cancelar">
                Cancelar
              </Button>
              <Button variant="outline" onClick={() => setVista(null)} data-testid="cargar-volver">
                Volver
              </Button>
              <Button onClick={guardar} disabled={guardando || seleccion.size === 0} data-testid="cargar-guardar" className="gap-1.5">
                {guardando ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
                Guardar {plural(seleccion.size, "pregunta", "preguntas")}
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
